import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon, LatLngBounds } from 'leaflet';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { friendsAPI, temporaryLocationsAPI, friendGroupsAPI } from '../services/api';
import { FriendLocation } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFriendsData } from '../contexts/FriendsDataContext';
import { getLocationTypes } from '../components/Map/locationTypes';
import { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale/fr';
import 'react-datepicker/dist/react-datepicker.css';
import 'leaflet/dist/leaflet.css';
import './Map.css';
// Import modals
import {
  AddToGroupModal
} from '../features/map/components/modals';
// Import FriendProfilePanel and ListPanel
import FriendProfilePanel from '../components/FriendProfilePanel';
import ListPanel from '../components/ListPanel';
import AddMembersModal from '../components/AddMembersModal';

// Import map utilities
import {
  userIcon,
  previewIcon,
  selectedPlaceIcon,
  createFriendIcon,
  calculateZoomForCluster,
  isPointOfInterest,
  getPhotoUrl
} from '../features/map/utils';

// Import map components
import {
  MapInstanceCapture,
  LocationSelector,
  ClusteredMarker,
  MapSearchBar,
  TempLocationFormManager,
  RoutePanel,
  RoutePolyline,
  FriendMarker
} from '../features/map/components';
import { MyTempLocationMarker } from '../features/map/components/MyTempLocationMarker';

// Import routing context and types
import { RouteProvider, useRoute } from '../contexts/RouteContext';
import type { RoutePoint } from '../services/routing';

// Import custom hooks
import {
  useMapState,
  useTempLocations,
  useMapFilters,
  useMapSearch,
  useAddressGeocoding,
  useGroupsManagement,
  useMapNavigation,
  useFriendClusters
} from '../features/map/hooks';

// Register French locale for date picker
registerLocale('fr', fr);

// Component to trigger routing from parent (bridge between Map state and RouteContext)
const RouteTrigger: React.FC<{
  destination: RoutePoint | null;
  onTriggered: () => void;
}> = ({ destination, onTriggered }) => {
  const { startRouteToDestination } = useRoute();

  useEffect(() => {
    if (destination) {
      startRouteToDestination(destination);
      onTriggered();
    }
  }, [destination, startRouteToDestination, onTriggered]);

  return null;
};

// Component to extract route state for hiding overlapping markers
// RoutePolyline renders all route markers, so we hide normal markers that would overlap
const RouteStateExtractor: React.FC<{
  onRouteStateChange: (state: {
    isRoutingMode: boolean;
    originMarkerType?: string;
    hasRoutePoints: boolean;
    routeFriendIds: number[]; // Friend IDs that are route points (to hide their normal markers)
  }) => void;
}> = ({ onRouteStateChange }) => {
  const { origin, destination, waypoints, isRoutingMode } = useRoute();

  useEffect(() => {
    // Collect all friend IDs from route points
    const routeFriendIds: number[] = [];
    if (origin?.friendId != null) routeFriendIds.push(origin.friendId);
    if (destination?.friendId != null) routeFriendIds.push(destination.friendId);
    waypoints.forEach(wp => {
      if (wp.friendId != null) routeFriendIds.push(wp.friendId);
    });

    onRouteStateChange({
      isRoutingMode,
      originMarkerType: origin?.markerType,
      hasRoutePoints: !!(origin || destination),
      routeFriendIds,
    });
  }, [origin, destination, waypoints, isRoutingMode, onRouteStateChange]);

  return null;
};

// Fix for default marker icons in React-Leaflet

delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const Map: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // State for showing/hiding the "no friends" panel
  const [showNoFriendsPanel, setShowNoFriendsPanel] = useState(true);

  // State for welcome modal (shown after registration)
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Check for welcome modal flag on mount
  useEffect(() => {
    const shouldShowWelcome = sessionStorage.getItem('showWelcomeModal');
    if (shouldShowWelcome) {
      sessionStorage.removeItem('showWelcomeModal');
      setShowWelcomeModal(true);
    }
  }, []);

  // State for friend profile panel (new bottom sheet panel)
  const [selectedFriendForPanel, setSelectedFriendForPanel] = useState<number | null>(null);

  // Listen for custom event to open own profile panel (from Navbar)
  useEffect(() => {
    const handleOpenOwnProfile = () => {
      if (user) {
        setSelectedFriendForPanel(user.id);
      }
    };

    window.addEventListener('openOwnProfilePanel', handleOpenOwnProfile);
    return () => window.removeEventListener('openOwnProfilePanel', handleOpenOwnProfile);
  }, [user]);

  // Handle navigation state to open own profile panel
  useEffect(() => {
    const state = location.state as { openOwnProfile?: boolean } | null;
    if (state?.openOwnProfile && user) {
      // Small delay to ensure map is ready
      setTimeout(() => {
        setSelectedFriendForPanel(user.id);
      }, 300);
      // Clear the state to prevent reopening on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, user, navigate, location.pathname]);
  // State for list panel
  const [selectedListForPanel, setSelectedListForPanel] = useState<number | null>(null);
  const [listMemberIds, setListMemberIds] = useState<number[]>([]);
  // Panel navigation history for back button
  const [panelHistory, setPanelHistory] = useState<Array<{type: 'friend' | 'list', id: number}>>([]);
  // Track panel height for button positioning (in vh)
  const [panelHeightVh, setPanelHeightVh] = useState(0);
  // State for highlighting temp location to open its popup
  const [highlightedTempLocationId, setHighlightedTempLocationId] = useState<number | null>(null);
  // State for "Add Members to List" modal
  const [showAddMembersToListModal, setShowAddMembersToListModal] = useState(false);
  const [selectedFriendIdsForList, setSelectedFriendIdsForList] = useState<number[]>([]);
  const [listPanelRefreshKey, setListPanelRefreshKey] = useState(0);
  const [newlyAddedMemberIds, setNewlyAddedMemberIds] = useState<number[]>([]);

  // State to track route info for hiding overlapping markers
  // RoutePolyline renders all route markers, so we hide normal markers that would overlap
  const [routeState, setRouteState] = useState<{
    isRoutingMode: boolean;
    originMarkerType?: string;
    hasRoutePoints: boolean;
    routeFriendIds: number[]; // Friend IDs that are route points (to hide their normal markers)
  }>({ isRoutingMode: false, hasRoutePoints: false, routeFriendIds: [] });

  // State to save selectedPlace when opening temp location modal
  // This allows us to restore the place panel if user closes modal without saving
  const [pendingPlacePanel, setPendingPlacePanel] = useState<any | null>(null);

  // State for routing - destination to navigate to
  const [pendingRouteDestination, setPendingRouteDestination] = useState<RoutePoint | null>(null);

  // Search history state
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('mapSearchHistory');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Get location types with translations
  const LOCATION_TYPES = getLocationTypes(t);

  // Core map state (managed by useMapState hook)
  const {
    locations, setLocations,
    loading, setLoading,
    error, setError,
    friendsDataContext,
    loadingFriend,
    userPosition, setUserPosition,
    geoError, setGeoError,
    mapInstance, setMapInstance,
    mapCenter, setMapCenter,
    mapZoom, setMapZoom,
    highlightedFriendId, setHighlightedFriendId,
  } = useMapState();

  // Temporary locations state (managed by useTempLocations hook)
  const {
    tempLocations,
    setTempLocations,
    myTempLocations,
    setMyTempLocations,
    showTempLocationModal,
    setShowTempLocationModal,
    tempLocationData,
    setTempLocationData,
    isSelectingLocation,
    setIsSelectingLocation,
    previewMarkerPosition,
    setPreviewMarkerPosition,
  } = useTempLocations();

  // Address search and geocoding (managed by useAddressGeocoding hook)
  const {
    addressSearchQuery,
    setAddressSearchQuery,
    addressSuggestions,
    setAddressSuggestions,
    isSearchingAddress,
    setIsSearchingAddress,
    skipNextSearch,
    setSkipNextSearch,
  } = useAddressGeocoding();

  // Groups management (managed by useGroupsManagement hook)
  const {
    groups,
    setGroups,
    friendGroups,
    setFriendGroups,
    showAddFriendToGroupModal,
    setShowAddFriendToGroupModal,
    selectedFriendForGroup,
    selectedGroupsForFriend,
    setSelectedGroupsForFriend,
  } = useGroupsManagement();

  // Map search functionality (managed by useMapSearch hook)
  const {
    showSearchBar,
    setShowSearchBar,
    searchQuery,
    setSearchQuery,
    friendResults,
    setFriendResults,
    placeResults,
    setPlaceResults,
    isSearching,
    setIsSearching,
    selectedPlace,
    setSelectedPlace,
    currentPositionPlace,
    setCurrentPositionPlace,
    selectedMapPlace,
    setSelectedMapPlace,
  } = useMapSearch();

  // Map filters (managed by useMapFilters hook)
  const {
    mapFilters,
    setMapFilters,
    showFiltersPanel,
    setShowFiltersPanel,
    isFiltersExpanded,
    setIsFiltersExpanded,
  } = useMapFilters();

  // Ref for filters panel to detect clicks outside
  const filtersPanelRef = useRef<HTMLDivElement>(null);

  // Close filters panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showFiltersPanel &&
        filtersPanelRef.current &&
        !filtersPanelRef.current.contains(event.target as Node)
      ) {
        // Also check if the click was on the toggle button (to avoid conflict)
        const target = event.target as HTMLElement;
        if (!target.closest('.toggle-filters-button')) {
          setShowFiltersPanel(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFiltersPanel, setShowFiltersPanel]);

  // Map navigation (managed by useMapNavigation hook)
  const {
    handleTempLocationClick,
  } = useMapNavigation(mapInstance, locations, setHighlightedFriendId);

  const loadFriendsLocations = async () => {
    // Load from Context (handles caching and state management)
    await friendsDataContext.loadFriendsLocations();

    // Extract and store friend groups from the locations response
    const groupsMap: Map<number, { id: number; name: string; color?: string }[]> = new globalThis.Map();
    locations.forEach(location => {
      if (location.groups && location.groups.length > 0) {
        groupsMap.set(location.id, location.groups);
      }
    });
    setFriendGroups(groupsMap);

    // Only set initial center if geolocation hasn't set it yet
    if (!userPosition) {
      if (user?.address?.latitude && user?.address?.longitude) {
        setMapCenter([user.address.latitude, user.address.longitude]);
        setMapZoom(10);
      } else if (locations.length > 0 && locations[0].latitude && locations[0].longitude) {
        setMapCenter([locations[0].latitude, locations[0].longitude]);
        setMapZoom(8);
      }
    }
  };

  async function loadTempLocations() {
    try {
      const { temporaryLocations } = await temporaryLocationsAPI.getFriendsActive();
      setTempLocations(temporaryLocations);
    } catch (err: any) {
      console.error('Failed to load temporary locations:', err);
    }
  }

  async function loadMyTempLocations() {
    try {
      // Load all temporary locations (active, upcoming, and past)
      const { temporaryLocations } = await temporaryLocationsAPI.getUserTemporaryLocations();
      setMyTempLocations(temporaryLocations);
    } catch (err: any) {
      console.error('Failed to load my temporary locations:', err);
    }
  }

  const loadGroups = async () => {
    try {
      const { groups: fetchedGroups } = await friendGroupsAPI.getUserGroupsWithMembers();
      setGroups(fetchedGroups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  // loadFriendGroups function removed - groups are now loaded directly with locations (no more N+1 queries!)

  const handleAddFriendToGroups = async () => {
    if (!selectedFriendForGroup) {
      return;
    }

    try {
      setLoading(true);
      const currentGroups = friendGroups.get(selectedFriendForGroup);
      const currentGroupIds = currentGroups ? currentGroups.map(g => g.id) : [];
      const groupsToAdd = selectedGroupsForFriend.filter(id => !currentGroupIds.includes(id));
      const groupsToRemove = currentGroupIds.filter(id => !selectedGroupsForFriend.includes(id));

      await Promise.all([
        ...groupsToAdd.map((groupId) =>
          friendGroupsAPI.addMembers(groupId, { friend_ids: [selectedFriendForGroup] })
        ),
        ...groupsToRemove.map((groupId) =>
          friendGroupsAPI.removeMember(groupId, selectedFriendForGroup)
        )
      ]);

      await loadGroups();
      await loadFriendsLocations(); // Reload locations with updated groups
      setShowAddFriendToGroupModal(false);
      setSelectedGroupsForFriend([]);
    } catch (error) {
      console.error('Failed to update groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupSelection = (groupId: number) => {
    setSelectedGroupsForFriend((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleOpenTempLocationModal = () => {
    console.log('📌 Button clicked, selectedPlace:', selectedPlace);
    // If a place is already selected, use it directly
    if (selectedPlace) {
      console.log('🎯 Selected place exists, opening modal directly');
      const lat = parseFloat(selectedPlace.lat);
      const lon = parseFloat(selectedPlace.lon);
      setTempLocationData({
        latitude: lat,
        longitude: lon,
        name: selectedPlace.display_name.split(',')[0],
        description: selectedPlace.display_name,
        start_datetime: new Date().toISOString().slice(0, 16),
        end_datetime: new Date(Date.now() + 3600000).toISOString().slice(0, 16), // +1 hour
        type: 'other',
      });
      setShowTempLocationModal(true);
      // Close the place panel
      setSelectedPlace(null);
    } else {
      // Activate selection mode if no place is selected
      console.log('🎯 No place selected, activating selection mode');
      setIsSelectingLocation(true);
      setPreviewMarkerPosition(null);
      console.log('✅ Selection mode now active, isSelectingLocation = true');
    }
  };

  const handleMapClick = async (e: any) => {
    console.log('🖱️ Map clicked, isSelectingLocation:', isSelectingLocation);

    if (!isSelectingLocation) {
      console.log('⚠️ Not in selection mode, ignoring click');
      return;
    }

    const { lat, lng } = e.latlng;
    console.log('📍 Click position:', lat, lng);

    setPreviewMarkerPosition([lat, lng]);

    // Close other panels when selecting a location with 📌
    setSelectedPlace(null);
    setCurrentPositionPlace(null);

    // Perform reverse geocoding to get address suggestion via backend proxy
    console.log('🌍 Starting reverse geocoding...');
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(
        `${apiBaseUrl}/search/reverse?lat=${lat}&lon=${lng}`
      );
      const data = await response.json();

      console.log('🗺️ Reverse geocoding response:', data);

      // Always build a clean, concise display name from address components
      let displayName = 'Lieu sélectionné';

      if (data && data.address) {
        const parts = [];

        // Try to build a meaningful address from components
        if (data.address.house_number && data.address.road) {
          parts.push(`${data.address.house_number} ${data.address.road}`);
        } else if (data.address.road) {
          parts.push(data.address.road);
        } else if (data.address.neighbourhood) {
          parts.push(data.address.neighbourhood);
        } else if (data.address.suburb) {
          parts.push(data.address.suburb);
        }

        // Add city/town/village
        const locality = data.address.city || data.address.town || data.address.village;
        if (locality && !parts.some(p => p.includes(locality))) {
          parts.push(locality);
        }

        // Add country if we have very little info
        if (parts.length <= 1 && data.address.country) {
          parts.push(data.address.country);
        }

        if (parts.length > 0) {
          displayName = parts.join(', ');
        } else if (data.display_name) {
          // Fallback to full display_name if we couldn't build from components
          displayName = data.display_name;
        }
      } else if (data && data.display_name) {
        displayName = data.display_name;
      }

      console.log('📍 Final display name:', displayName);

      const placeData = {
        lat: lat.toString(),
        lon: lng.toString(),
        display_name: displayName,
        address: data.address || null,
      };

      console.log('✅ Setting selectedMapPlace:', placeData);
      setSelectedMapPlace(placeData);
    } catch (error) {
      console.error('❌ Reverse geocoding failed:', error);
      // Set fallback data if geocoding fails
      const fallbackData = {
        lat: lat.toString(),
        lon: lng.toString(),
        display_name: 'Lieu sélectionné',
        address: null,
      };
      console.log('⚠️ Setting fallback selectedMapPlace:', fallbackData);
      setSelectedMapPlace(fallbackData);
    }
  };

  const handleConfirmLocation = () => {
    if (!previewMarkerPosition) return;

    const [lat, lng] = previewMarkerPosition;
    setTempLocationData({
      latitude: lat,
      longitude: lng,
      name: '',
      description: '',
      start_datetime: new Date().toISOString().slice(0, 16),
      end_datetime: new Date(Date.now() + 3600000).toISOString().slice(0, 16), // +1 hour
      type: 'other',
      visibility_type: 'all',
      visible_group_ids: [],
      visible_friend_ids: [],
    });

    // Pre-fill the address search field with the selected map place address
    if (selectedMapPlace) {
      setAddressSearchQuery(selectedMapPlace.display_name || '');
    }

    setShowTempLocationModal(true);
    setIsSelectingLocation(false);
    setPreviewMarkerPosition(null);
    // Keep selectedMapPlace open so user can go back to it if they cancel
    setAddressSuggestions([]);
  };

  const handleCancelSelection = () => {
    setIsSelectingLocation(false);
    setPreviewMarkerPosition(null);
  };

  // Wrapper function to create group from temp location modal
  const handleCreateGroupFromTempModal = async (name: string, color: string): Promise<number> => {
    setLoading(true);
    try {
      const response = await friendGroupsAPI.create({ name, color });
      const newGroupId = response.group.id;
      await loadGroups(); // Reload groups
      return newGroupId;
    } catch (error) {
      console.error('Failed to create group:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Address search with Nominatim API via backend proxy
  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setIsSearchingAddress(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(
        `${apiBaseUrl}/search/places?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      setAddressSuggestions(data);
    } catch (error) {
      console.error('Address search failed:', error);
      setAddressSuggestions([]);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const selectAddress = (suggestion: any) => {
    const lat = parseFloat(suggestion.lat);
    const lng = parseFloat(suggestion.lon);

    setTempLocationData({
      ...tempLocationData,
      latitude: lat,
      longitude: lng,
    });

    setAddressSearchQuery(suggestion.display_name);
    setAddressSuggestions([]);

    // Update preview marker position if in selection mode
    if (isSelectingLocation) {
      setPreviewMarkerPosition([lat, lng]);
    }

    // Pan map to selected location
    if (mapInstance) {
      mapInstance.setView([lat, lng], 15, {
        animate: true,
        duration: 1
      });
    }
  };

  // Debounce search to avoid too many API calls
  useEffect(() => {
    // Skip search if flag is set (when pre-filling address)
    if (skipNextSearch) {
      setSkipNextSearch(false);
      return;
    }

    const timer = setTimeout(() => {
      if (addressSearchQuery) {
        searchAddress(addressSearchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressSearchQuery]);

  async function requestGeolocation() {
    const isNative = Capacitor.isNativePlatform();

    const handleSuccess = (latitude: number, longitude: number) => {
      setUserPosition([latitude, longitude]);
      setMapCenter([latitude, longitude]);
      setMapZoom(12);
      setGeoError('');
    };

    const handleError = (message: string) => {
      const fallback = t('map.gps.errors.fallback');
      setGeoError(`${message}\n${fallback}`);

      // Fallback to user's address if geolocation fails
      if (user?.address?.latitude && user?.address?.longitude) {
        setMapCenter([user.address.latitude, user.address.longitude]);
        setMapZoom(10);
      }
    };

    if (isNative) {
      // Use Capacitor Geolocation plugin on native platforms
      try {
        // Check permission first
        const permStatus = await Geolocation.checkPermissions();
        console.log('Geolocation permission status:', permStatus);

        if (permStatus.location !== 'granted' && permStatus.coarseLocation !== 'granted') {
          // Request permission
          const requestStatus = await Geolocation.requestPermissions();
          console.log('After request, permission status:', requestStatus);

          if (requestStatus.location !== 'granted' && requestStatus.coarseLocation !== 'granted') {
            handleError(t('map.gps.errors.permissionDenied'));
            return;
          }
        }

        // Get current position
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
        });

        handleSuccess(position.coords.latitude, position.coords.longitude);
      } catch (error: any) {
        console.error('Capacitor Geolocation error:', error);
        if (error.message?.includes('permission') || error.message?.includes('denied')) {
          handleError(t('map.gps.errors.permissionDenied'));
        } else if (error.message?.includes('timeout')) {
          handleError(t('map.gps.errors.timeout'));
        } else {
          handleError(t('map.gps.errors.positionUnavailable'));
        }
      }
    } else {
      // Use browser geolocation API on web
      if (!navigator.geolocation) {
        setGeoError('La géolocalisation n\'est pas supportée par votre navigateur');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleSuccess(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Browser Geolocation error:', error);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              handleError(t('map.gps.errors.permissionDenied'));
              break;
            case error.POSITION_UNAVAILABLE:
              handleError(t('map.gps.errors.positionUnavailable'));
              break;
            case error.TIMEOUT:
              handleError(t('map.gps.errors.timeout'));
              break;
            default:
              handleError(t('map.gps.errors.unknown'));
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    }
  }

  useEffect(() => {
    const initializeMap = async () => {
      // Only load if not already cached in Context (avoid loading screen on navigation)
      if (friendsDataContext.locations.length === 0) {
        await loadFriendsLocations(); // Now loads groups directly - no more N+1 queries!
      }
      await loadGroups();
    };

    initializeMap();
    loadTempLocations();
    loadMyTempLocations();
    requestGeolocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling delta for temporary locations every 3 minutes
  useEffect(() => {
    let lastSync = Date.now();

    const loadTempLocationsDelta = async () => {
      // Only poll if page is visible
      if (document.visibilityState !== 'visible') return;

      try {
        const { temporaryLocations: deltaLocations } = await temporaryLocationsAPI.getFriendsActiveDelta(lastSync);

        if (deltaLocations.length > 0) {
          // Merge delta changes into existing temp locations
          setTempLocations(prev => {
            const map = new globalThis.Map(prev.map(t => [t.id, t]));
            deltaLocations.forEach(t => map.set(t.id, t));
            return Array.from(map.values());
          });
        }

        lastSync = Date.now();
      } catch (err: any) {
        console.error('Failed to load temp locations delta:', err);
        // Don't show error to user - just log it
      }
    };

    // Initial delta load
    loadTempLocationsDelta();

    // Poll every 3 minutes (180000ms)
    const pollingInterval = setInterval(loadTempLocationsDelta, 180000);

    return () => clearInterval(pollingInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Friend groups are now loaded directly with locations - this useEffect is no longer needed!

  // Handle friend address centering from navigation (from Friends page)
  useEffect(() => {
    if (locations.length === 0) return;

    const mapFriendFilter = localStorage.getItem('mapFriendFilter');
    const mapAddressType = localStorage.getItem('mapAddressType');
    const mapTempLocationId = localStorage.getItem('mapTempLocationId');
    const shouldOpenPopup = localStorage.getItem('openPopup');

    if (mapFriendFilter && mapAddressType) {
      const friendId = parseInt(mapFriendFilter);
      const friend = locations.find(loc => loc.id === friendId);

      if (friend) {
        let lat: number | null = null;
        let lng: number | null = null;

        if (mapAddressType === 'official') {
          // Use friend's official address
          if (friend.latitude && friend.longitude) {
            lat = friend.latitude;
            lng = friend.longitude;
          }
        } else if (mapAddressType === 'current' && mapTempLocationId) {
          // Use friend's current temporary location
          const tempLocationId = parseInt(mapTempLocationId);
          const tempLocation = tempLocations.find(loc => loc.id === tempLocationId && loc.friend_id === friendId);

          if (tempLocation && tempLocation.latitude && tempLocation.longitude) {
            lat = tempLocation.latitude;
            lng = tempLocation.longitude;
          }
        }

        // Center the map on the coordinates
        if (lat !== null && lng !== null && mapInstance) {
          // If opening popup, use a high zoom level to ensure cluster separation
          // Otherwise use default zoom
          const targetZoom = shouldOpenPopup === 'true' ? 17 : 13;

          mapInstance.flyTo([lat, lng], targetZoom, {
            duration: 1.5
          });

          // Open popup if requested (wait for map animation to complete)
          if (shouldOpenPopup === 'true') {
            setTimeout(() => {
              setHighlightedFriendId(friendId);
              // Keep highlight active longer to ensure popup opens and stays open
              setTimeout(() => {
                setHighlightedFriendId(null);
              }, 2000);
            }, 1700); // Wait for flyTo animation to complete
          }
        }
      }

      // Clean up localStorage
      localStorage.removeItem('mapFriendFilter');
      localStorage.removeItem('mapAddressType');
      localStorage.removeItem('mapTempLocationId');
      localStorage.removeItem('openPopup');
    }
  }, [locations, tempLocations, mapInstance, setHighlightedFriendId]);

  // Handle friend profile view from Friends page (filter to show only one friend)
  useEffect(() => {
    if (locations.length === 0) return;

    const mapSelectedFriend = localStorage.getItem('mapSelectedFriend');
    const openFriendPanel = localStorage.getItem('openFriendPanel');

    if (mapSelectedFriend) {
      const friendId = parseInt(mapSelectedFriend);
      const friend = locations.find(loc => loc.id === friendId);

      if (friend) {
        // Set filter to show only this friend
        setMapFilters({
          ...mapFilters,
          selectedFriendId: friendId,
        });

        // Center map on friend's address
        if (friend.latitude && friend.longitude && mapInstance) {
          mapInstance.flyTo([friend.latitude, friend.longitude], 13, {
            duration: 1.5
          });
        }

        // Open friend profile panel if requested
        if (openFriendPanel === 'true') {
          setTimeout(() => {
            setSelectedFriendForPanel(friendId);
          }, 500); // Small delay to ensure map is ready
        }
      }

      // Clean up localStorage
      localStorage.removeItem('mapSelectedFriend');
      localStorage.removeItem('openFriendPanel');
    }
  }, [locations, mapInstance, setMapFilters, mapFilters]);

  // Handle list panel open from Friends page (via "Show on map" menu option)
  const processedGroupFilterRef = useRef<string | null>(null);
  const pendingListFitBoundsRef = useRef<number | null>(null);

  useEffect(() => {
    const mapGroupFilter = localStorage.getItem('mapGroupFilter');

    if (!mapGroupFilter) {
      return;
    }

    // Create unique key combining location and group to avoid duplicate processing
    const processingKey = `${location.key}-${mapGroupFilter}`;

    // Skip if already processed this exact navigation + filter combo
    if (processedGroupFilterRef.current === processingKey) {
      return;
    }

    // Mark as processed before removing from localStorage
    processedGroupFilterRef.current = processingKey;
    localStorage.removeItem('mapGroupFilter');

    const groupId = parseInt(mapGroupFilter);

    // Store the pending fit bounds request
    pendingListFitBoundsRef.current = groupId;

    // Small delay to ensure component is fully initialized
    setTimeout(() => {
      // If locations are already loaded, fit bounds now
      // Otherwise, handleListClick will be called but fitBounds will be handled by the next effect
      handleListClick(groupId, locations.length > 0);
    }, 150);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]); // Re-run when navigating to this page

  // Handle pending fit bounds when locations become available
  useEffect(() => {
    if (pendingListFitBoundsRef.current && locations.length > 0 && mapInstance && listMemberIds.length > 0) {
      const memberCoords = locations
        .filter(loc => listMemberIds.includes(loc.id) && loc.latitude && loc.longitude)
        .map(loc => [loc.latitude, loc.longitude] as [number, number]);

      if (memberCoords.length > 0) {
        mapInstance.closePopup();
        const panelWidth = window.innerWidth > 768 ? 400 : 0;

        if (memberCoords.length === 1) {
          const [lat, lng] = memberCoords[0];
          const targetZoom = 13;
          const targetPoint = mapInstance.project([lat, lng], targetZoom);
          const offsetPoint = targetPoint.subtract([panelWidth / 2, 0]);
          const offsetLatLng = mapInstance.unproject(offsetPoint, targetZoom);
          mapInstance.flyTo(offsetLatLng, targetZoom, { animate: true, duration: 1 });
        } else {
          const bounds = new LatLngBounds(memberCoords);
          mapInstance.fitBounds(bounds, {
            padding: [50, 50],
            paddingTopLeft: [panelWidth, 0],
            maxZoom: 15,
            animate: true
          });
        }
      }

      // Clear the pending request
      pendingListFitBoundsRef.current = null;
    }
  }, [locations, listMemberIds, mapInstance]);

  // Handle window resize for search bar visibility
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowSearchBar(true); // Always show on desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-close search bar on mobile when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Only on mobile
      if (window.innerWidth > 768) return;

      // Only if search bar is open and empty
      if (!showSearchBar || searchQuery.trim()) return;

      const target = e.target as HTMLElement;

      // Don't close if clicking on the search bar itself or the toggle button
      const searchBar = document.querySelector('.main-search-bar');
      const toggleButton = document.querySelector('.search-toggle-button');

      if (searchBar && searchBar.contains(target)) return;
      if (toggleButton && toggleButton.contains(target)) return;

      // Close the search bar
      setShowSearchBar(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearchBar, searchQuery]);

  // Unified autocomplete search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFriendResults([]);
      setPlaceResults([]);
      return;
    }

    console.log('⏱️ Search debounce started for:', searchQuery);
    const timer = setTimeout(() => {
      console.log('🚀 Launching search for:', searchQuery);
      // Search both friends and places simultaneously
      searchFriends(searchQuery);
      searchPlaces(searchQuery);
    }, 300); // 300ms debounce - optimized for better responsiveness

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Handle edit from profile page (query parameter)
  useEffect(() => {
    const editId = searchParams.get('editTempLocation');
    if (editId) {
      const loadTempLocationForEdit = async () => {
        try {
          const id = parseInt(editId, 10);

          // First check if we have cached data in localStorage (preserves address)
          const cachedData = localStorage.getItem('editingTempLocation');
          let temporaryLocation;

          if (cachedData) {
            temporaryLocation = JSON.parse(cachedData);
            // Clear the cached data after using it
            localStorage.removeItem('editingTempLocation');
          } else {
            // Fallback to API if no cached data
            const response = await temporaryLocationsAPI.getById(id);
            temporaryLocation = response.temporaryLocation;
          }

          setTempLocationData({
            id: temporaryLocation.id,
            latitude: temporaryLocation.latitude,
            longitude: temporaryLocation.longitude,
            name: temporaryLocation.name,
            description: temporaryLocation.description,
            start_datetime: temporaryLocation.start_datetime,
            end_datetime: temporaryLocation.end_datetime,
            type: temporaryLocation.type,
            visibility_type: temporaryLocation.visibility_type,
            visible_group_ids: temporaryLocation.visible_group_ids || [],
            visible_friend_ids: temporaryLocation.visible_friend_ids || [],
            is_active: temporaryLocation.is_active,
          });

          // Set the address in the search field if available
          if (temporaryLocation.address) {
            setSkipNextSearch(true); // Don't trigger suggestions for pre-filled address
            setAddressSearchQuery(temporaryLocation.address);
          }

          // Center map on the location
          if (mapInstance && temporaryLocation.latitude && temporaryLocation.longitude) {
            mapInstance.flyTo([temporaryLocation.latitude, temporaryLocation.longitude], 13);
          }

          setShowTempLocationModal(true);
          // Clear the query parameter
          setSearchParams({});
        } catch (error) {
          console.error('Failed to load temporary location for editing:', error);
          alert(t('map.tempLocation.loadFailed'));
        }
      };
      loadTempLocationForEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, mapInstance]);

  // Handle create from profile page (query parameter)
  useEffect(() => {
    const createTempLocation = searchParams.get('createTempLocation');
    if (createTempLocation === 'true') {
      // Initialize with default values for a new temp location
      setTempLocationData({
        latitude: 0,
        longitude: 0,
        name: '',
        description: '',
        start_datetime: new Date().toISOString().slice(0, 16),
        end_datetime: new Date(Date.now() + 3600000).toISOString().slice(0, 16), // +1 hour
        type: 'other',
        visibility_type: 'all',
        visible_group_ids: [],
        visible_friend_ids: [],
      });

      // Clear address search field
      setAddressSearchQuery('');

      // Open the modal
      setShowTempLocationModal(true);

      // Clear the query parameter
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleContactClick = async (friendId: number) => {
    // Open new friend profile panel instead of old modal
    setSelectedFriendForPanel(friendId);
  };

  const handleCloseFriendPanel = () => {
    setSelectedFriendForPanel(null);
    setPanelHeightVh(0);
    setPanelHistory([]); // Clear history when fully closing
  };

  const handlePanelAddressClick = (lat: number, lng: number, tempLocationId?: number, isOwnTempLocation: boolean = true) => {
    // Center map and zoom when clicking an address in the panel
    if (mapInstance) {
      // Close any open popup to prevent autoPan returning to it
      mapInstance.closePopup();

      // If temp location ID is provided, highlight it to open popup
      if (tempLocationId !== undefined) {
        // For friend temp locations in clusters, add offset to match their clustered ID
        // Own temp locations use raw ID (rendered separately via MyTempLocationMarker)
        const highlightId = isOwnTempLocation ? tempLocationId : tempLocationId + 1000000;
        setHighlightedTempLocationId(highlightId);
        // Reset after popup opens
        setTimeout(() => {
          setHighlightedTempLocationId(null);
        }, 500);
      }

      const isDesktop = window.innerWidth > 768;
      const targetZoom = 15;
      const targetPoint = mapInstance.project([lat, lng], targetZoom);

      let offsetX = 0;
      let offsetY = 0;

      if (isDesktop) {
        // Desktop: Panel is ~400px wide on the left, offset center to the right
        const panelWidth = 400;
        offsetX = panelWidth / 2;
      } else {
        // Mobile: Small offset to account for popup opening above marker
        // Keep marker slightly below center so popup fits in visible area
        const screenHeight = window.innerHeight;
        offsetY = screenHeight * 0.05; // Small offset (5% of screen)
      }

      // Apply offset
      const offsetPoint = targetPoint.subtract([offsetX, -offsetY]);
      const offsetLatLng = mapInstance.unproject(offsetPoint, targetZoom);

      mapInstance.flyTo(offsetLatLng, targetZoom, {
        animate: true,
        duration: 1
      });

      // Open the popup for the current friend after animation completes
      if (selectedFriendForPanel) {
        setTimeout(() => {
          setHighlightedFriendId(selectedFriendForPanel);
          // Reset highlight after popup is open
          setTimeout(() => {
            setHighlightedFriendId(null);
          }, 1500);
        }, 1100); // Wait for flyTo animation
      }
    }
  };

  // List panel handlers
  const handleListClick = async (listId: number, fitBoundsToMembers: boolean = false) => {
    try {
      // Save current friend panel to history before switching
      if (selectedFriendForPanel) {
        setPanelHistory(prev => [...prev, { type: 'friend', id: selectedFriendForPanel }]);
      }

      // Close friend panel and open list panel
      setSelectedFriendForPanel(null);
      setSelectedListForPanel(listId);

      // Load list members to filter the map
      const { group } = await friendGroupsAPI.getGroupWithMembers(listId);
      const memberIds = group.members.map(m => m.id);
      setListMemberIds(memberIds);

      // Update map filters to show only list members
      setMapFilters({
        ...mapFilters,
        selectedFriendId: null,
        selectedListId: listId,
      });

      // Fit map bounds to show all list members (when opening from /friends)
      if (fitBoundsToMembers && mapInstance && memberIds.length > 0) {
        // Get coordinates of all members from locations
        const memberCoords = locations
          .filter(loc => memberIds.includes(loc.id) && loc.latitude && loc.longitude)
          .map(loc => [loc.latitude, loc.longitude] as [number, number]);

        if (memberCoords.length > 0) {
          // Close any open popup
          mapInstance.closePopup();

          // Calculate panel offset for desktop
          const panelWidth = window.innerWidth > 768 ? 400 : 0;

          if (memberCoords.length === 1) {
            // Single member - just center on them with offset
            const [lat, lng] = memberCoords[0];
            const targetZoom = 13;
            const targetPoint = mapInstance.project([lat, lng], targetZoom);
            const offsetPoint = targetPoint.subtract([panelWidth / 2, 0]);
            const offsetLatLng = mapInstance.unproject(offsetPoint, targetZoom);
            mapInstance.flyTo(offsetLatLng, targetZoom, { animate: true, duration: 1 });
          } else {
            // Multiple members - fit bounds with padding
            const bounds = new LatLngBounds(memberCoords);
            mapInstance.fitBounds(bounds, {
              padding: [50, 50],
              paddingTopLeft: [panelWidth, 0], // Account for side panel
              maxZoom: 15,
              animate: true
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to load list:', error);
    }
  };

  const handleCloseListPanel = () => {
    setSelectedListForPanel(null);
    setListMemberIds([]);
    setPanelHeightVh(0);
    setPanelHistory([]); // Clear history when fully closing
    // Clear list filter
    setMapFilters({
      ...mapFilters,
      selectedListId: null,
    });
  };

  const handleListMemberClick = (friendId: number) => {
    // Save current list panel to history before switching
    if (selectedListForPanel) {
      setPanelHistory(prev => [...prev, { type: 'list', id: selectedListForPanel }]);
    }

    // Close list panel and open friend panel
    setSelectedListForPanel(null);
    setSelectedFriendForPanel(friendId);
    // Keep the list filter active so we still see only list members on the map
  };

  // Open "Add Members to List" modal
  const handleOpenAddMembersModal = () => {
    setSelectedFriendIdsForList([]);
    setShowAddMembersToListModal(true);
  };

  // Get friends who are not already in the current list
  const getAvailableFriendsForList = () => {
    if (!selectedListForPanel) return [];
    return locations.filter(friend => !listMemberIds.includes(friend.id));
  };

  // Toggle friend selection for adding to list
  const toggleFriendSelectionForList = (friendId: number) => {
    setSelectedFriendIdsForList(prev =>
      prev.includes(friendId)
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  // Add selected friends to the current list
  const handleAddMembersToList = async () => {
    if (!selectedListForPanel || selectedFriendIdsForList.length === 0) return;

    try {
      setLoading(true);
      await friendGroupsAPI.addMembers(selectedListForPanel, { friend_ids: selectedFriendIdsForList });

      // Reload the list members
      const { group } = await friendGroupsAPI.getGroupWithMembers(selectedListForPanel);
      setListMemberIds(group.members.map((m: any) => m.id));

      // Close modal
      setShowAddMembersToListModal(false);

      // Store the IDs of newly added members for animation
      const addedIds = [...selectedFriendIdsForList];
      setSelectedFriendIdsForList([]);

      // Set newly added member IDs before refresh so animation triggers
      setNewlyAddedMemberIds(addedIds);

      // Trigger ListPanel refresh
      setListPanelRefreshKey(prev => prev + 1);

      // Reload groups to update badges
      const { groups: fetchedGroups } = await friendGroupsAPI.getUserGroupsWithMembers();
      setGroups(fetchedGroups);
    } catch (error) {
      console.error('Failed to add members to list:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate back in panel history
  const handlePanelBack = async () => {
    if (panelHistory.length === 0) return;

    const newHistory = [...panelHistory];
    const previousPanel = newHistory.pop()!;
    setPanelHistory(newHistory);

    if (previousPanel.type === 'friend') {
      // Going back to a friend panel
      setSelectedListForPanel(null);
      setSelectedFriendForPanel(previousPanel.id);
      // If no more list panels in history, clear the list filter
      const hasListInHistory = newHistory.some(h => h.type === 'list');
      if (!hasListInHistory) {
        setListMemberIds([]);
        setMapFilters({
          ...mapFilters,
          selectedListId: null,
        });
      }
    } else if (previousPanel.type === 'list') {
      // Going back to a list panel
      setSelectedFriendForPanel(null);
      setSelectedListForPanel(previousPanel.id);

      // Reload list members for the filter
      try {
        const { group } = await friendGroupsAPI.getGroupWithMembers(previousPanel.id);
        const memberIds = group.members.map(m => m.id);
        setListMemberIds(memberIds);
        setMapFilters({
          ...mapFilters,
          selectedFriendId: null,
          selectedListId: previousPanel.id,
        });
      } catch (error) {
        console.error('Failed to load list:', error);
      }
    }
  };

  const openDirectionsFromPopup = (info: {
    lat: number;
    lng: number;
    name?: string;
    address?: string;
    profilePhoto?: string;
    firstName?: string;
    lastName?: string;
    friendId?: number;
  }) => {
    // Use internal routing system
    setPendingRouteDestination({
      lat: info.lat,
      lng: info.lng,
      name: info.name,
      address: info.address,
      markerType: 'friend',
      profilePhoto: info.profilePhoto,
      firstName: info.firstName,
      lastName: info.lastName,
      friendId: info.friendId, // For FriendMarker to know not to render
    });
  };

  const centerOnUserPosition = async () => {
    if (userPosition && mapInstance) {
      mapInstance.setView(userPosition, 14, {
        animate: true,
        duration: 1
      });

      // Perform reverse geocoding to get address via backend proxy
      try {
        const [lat, lng] = userPosition;
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(
          `${apiBaseUrl}/search/reverse?lat=${lat}&lon=${lng}`
        );
        const data = await response.json();

        // Store the current position place data
        setCurrentPositionPlace({
          lat: lat.toString(),
          lon: lng.toString(),
          display_name: data.display_name || t('map.gps.myCurrentPosition'),
          address: data.address,
        });

        // Close any selected place panel
        setSelectedPlace(null);
        setSelectedMapPlace(null);
      } catch (error) {
        console.error('Reverse geocoding failed:', error);
      }
    } else {
      requestGeolocation();
    }
  };

  // Search functions
  const searchFriends = (query: string) => {
    if (!query.trim()) {
      setFriendResults([]);
      return;
    }

    const filtered = locations.filter(friend =>
      `${friend.first_name} ${friend.last_name}`.toLowerCase().includes(query.toLowerCase())
    );

    setFriendResults(filtered);
  };

  const searchPlaces = async (query: string) => {
    if (!query.trim()) {
      setPlaceResults([]);
      return;
    }

    console.log('🔍 Searching places for:', query);
    setIsSearching(true);
    try {
      // Use backend proxy to avoid CORS issues
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      // Build URL with geographic prioritization if user position is available
      let searchUrl = `${apiBaseUrl}/search/places?q=${encodeURIComponent(query)}&limit=10`;

      if (userPosition) {
        const [lat, lon] = userPosition;
        searchUrl += `&lat=${lat}&lon=${lon}&radius=50`;
        console.log('🌍 Search prioritizing results near:', lat, lon);
      }

      const response = await fetch(searchUrl);

      console.log('🌐 Response status:', response.status, response.statusText);

      if (!response.ok) {
        console.error('❌ API error:', response.status, response.statusText);
        setPlaceResults([]);
        return;
      }

      const data = await response.json();
      console.log('📍 Places found:', data.length, 'results', data);
      if (data.length > 0) {
        console.log('📦 First result sample:', data[0]);
      }

      setPlaceResults(data);
      console.log('✅ placeResults state updated with', data.length, 'items');
    } catch (error) {
      console.error('❌ Error searching places:', error);
      setPlaceResults([]);
    } finally {
      setIsSearching(false);
      console.log('🏁 Search completed, isSearching set to false');
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    // Let the useEffect handle the search with debounce
  };

  const selectFriendFromSearch = (friend: FriendLocation) => {
    if (friend.latitude && friend.longitude && mapInstance) {
      // Close any open popups and panels first
      mapInstance.closePopup();
      setSelectedPlace(null);
      setCurrentPositionPlace(null);

      // Capture coordinates (TypeScript knows these are defined after the check above)
      const friendLat = friend.latitude;
      const friendLng = friend.longitude;

      // Calculate optimal zoom to separate friend from nearby markers
      // Create a temporary cluster with this friend to check if others are nearby
      const friendAsCluster = {
        id: `search-${friend.id}`,
        friends: [friend],
        latitude: friendLat,
        longitude: friendLng,
        center: [friendLat, friendLng] as [number, number],
      };

      // Find nearby friends at zoom 14 (base zoom for search)
      const baseZoom = 14;
      const nearbyFriends = locations.filter(loc => {
        if (loc.id === friend.id || !loc.latitude || !loc.longitude) return false;
        // Check if within ~500m (roughly 0.005 degrees)
        const latDiff = Math.abs(loc.latitude - friendLat);
        const lngDiff = Math.abs(loc.longitude - friendLng);
        return latDiff < 0.005 && lngDiff < 0.005;
      });

      let targetZoom = baseZoom;

      if (nearbyFriends.length > 0) {
        // Create a cluster with all nearby friends to calculate needed zoom
        const clusterWithNearby = {
          ...friendAsCluster,
          friends: [friend, ...nearbyFriends],
        };
        // Calculate zoom needed to separate markers (minimum zoom 15 to disable clustering)
        targetZoom = Math.max(
          calculateZoomForCluster(clusterWithNearby, baseZoom, 30),
          15 // Ensure we reach zoom level where clustering is disabled
        );
        // Cap at zoom 17 to avoid being too close
        targetZoom = Math.min(targetZoom, 17);
      }

      // 1. Center on friend immediately
      mapInstance.setView([friend.latitude, friend.longitude], targetZoom, {
        animate: true,
        duration: 1
      });

      // 2. On desktop, pan to offset for sidebar (450px / 2 = 225px to the right)
      if (window.innerWidth > 768) {
        setTimeout(() => {
          mapInstance.panBy([-225, 0], { animate: true, duration: 0.5 });
        }, 100);
      }

      // 3. Open popup after pan animation completes (desktop: 600ms, mobile: 100ms)
      const popupDelay = window.innerWidth > 768 ? 600 : 100;
      setTimeout(() => {
        setHighlightedFriendId(friend.id);
        // Reset highlight after popup opens
        setTimeout(() => {
          setHighlightedFriendId(null);
        }, 500);
      }, popupDelay);

      // Open the friend profile panel
      setSelectedFriendForPanel(friend.id);

      // Add to search history
      if (searchQuery) {
        addToSearchHistory(searchQuery);
      }

      // Keep the friend name in the search bar
      setSearchQuery(`${friend.first_name} ${friend.last_name}`);
      setFriendResults([]);
      setPlaceResults([]);
      // Close search bar only on mobile
      if (window.innerWidth <= 768) {
        setShowSearchBar(false);
      }
    }
  };

  /**
   * Add query to search history (max 10 items, most recent first)
   */
  const addToSearchHistory = (query: string) => {
    if (!query || query.trim().length < 2) return;

    const trimmedQuery = query.trim();
    setSearchHistory(prev => {
      // Remove duplicates and add to front
      const filtered = prev.filter(item => item.toLowerCase() !== trimmedQuery.toLowerCase());
      const newHistory = [trimmedQuery, ...filtered].slice(0, 10); // Keep max 10 items

      // Save to localStorage
      try {
        localStorage.setItem('mapSearchHistory', JSON.stringify(newHistory));
      } catch (error) {
        console.error('Failed to save search history:', error);
      }

      return newHistory;
    });
  };

  const selectPlaceFromSearch = (place: any) => {
    if (place.lat && place.lon && mapInstance) {
      // Close any friend popup and current position panel
      setHighlightedFriendId(null);
      setCurrentPositionPlace(null);

      const lat = parseFloat(place.lat);
      const lon = parseFloat(place.lon);
      mapInstance.setView([lat, lon], 15, {
        animate: true,
        duration: 1
      });
      // Store the selected place
      setSelectedPlace(place);

      // Add to search history
      if (searchQuery) {
        addToSearchHistory(searchQuery);
      }

      // Keep the place name in the search bar
      setSearchQuery(place.display_name.split(',')[0]);
      setFriendResults([]);
      setPlaceResults([]);
      // Close other panels
      setCurrentPositionPlace(null);
      setSelectedMapPlace(null);
      // Close search bar only on mobile
      if (window.innerWidth <= 768) {
        setShowSearchBar(false);
      }
    }
  };

  const handleAddPlaceAsTempLocation = () => {
    if (!selectedPlace) return;

    const lat = parseFloat(selectedPlace.lat);
    const lon = parseFloat(selectedPlace.lon);
    const isPOI = isPointOfInterest(selectedPlace);

    console.log(`📍 Adding place as temp location - isPOI: ${isPOI}, type: ${selectedPlace.type}, class: ${selectedPlace.class}`);

    // Pre-fill the temp location form with place data
    // Only pre-fill name if it's a POI (restaurant, museum, etc.)
    // Leave empty for simple addresses (streets, roads)
    // Description is always left empty for user to fill
    setTempLocationData({
      latitude: lat,
      longitude: lon,
      name: isPOI ? (selectedPlace.name || selectedPlace.display_name.split(',')[0]) : '',
      description: '', // Always empty - user fills manually
      start_datetime: new Date().toISOString().slice(0, 16),
      end_datetime: new Date(Date.now() + 3600000).toISOString().slice(0, 16), // +1 hour
      type: 'other',
    });
    // Pre-fill the address search field without triggering search
    setSkipNextSearch(true);
    setAddressSearchQuery(selectedPlace.display_name);
    setAddressSuggestions([]); // Clear suggestions
    // Save selectedPlace before closing the panel
    setPendingPlacePanel(selectedPlace);
    setSelectedPlace(null); // Close the panel temporarily
    setShowTempLocationModal(true);
  };

  const handleAddCurrentPositionAsTempLocation = () => {
    if (!currentPositionPlace) return;

    const lat = parseFloat(currentPositionPlace.lat);
    const lon = parseFloat(currentPositionPlace.lon);

    // Pre-fill the temp location form with current position data
    setTempLocationData({
      latitude: lat,
      longitude: lon,
      name: t('map.gps.myCurrentPosition'),
      description: '',
      start_datetime: new Date().toISOString().slice(0, 16),
      end_datetime: new Date(Date.now() + 3600000).toISOString().slice(0, 16), // +1 hour
      type: 'other',
      visibility_type: 'all',
      visible_group_ids: [],
      visible_friend_ids: [],
    });
    // Pre-fill the address search field without triggering search
    setSkipNextSearch(true);
    setAddressSearchQuery(currentPositionPlace.display_name);
    setAddressSuggestions([]);
    setShowTempLocationModal(true);
    setCurrentPositionPlace(null);
  };

  const handleAddSelectedMapPlaceAsTempLocation = () => {
    if (!selectedMapPlace) return;

    const lat = parseFloat(selectedMapPlace.lat);
    const lon = parseFloat(selectedMapPlace.lon);

    // Pre-fill only coordinates and default values, user fills name and description
    setTempLocationData({
      latitude: lat,
      longitude: lon,
      name: '',
      description: '',
      start_datetime: new Date().toISOString().slice(0, 16),
      end_datetime: new Date(Date.now() + 3600000).toISOString().slice(0, 16), // +1 hour
      type: 'other',
      visibility_type: 'all',
      visible_group_ids: [],
      visible_friend_ids: [],
    });
    // Pre-fill the address search field without triggering search
    setSkipNextSearch(true);
    setAddressSearchQuery(selectedMapPlace.display_name || '');
    setAddressSuggestions([]);
    setShowTempLocationModal(true);
    // Keep selectedMapPlace open so user can go back to it if they cancel
    setIsSelectingLocation(false);
    setPreviewMarkerPosition(null);
  };

  // Cluster nearby friend markers based on current zoom level
  // RoutePolyline handles rendering of route markers, no need to exclude from clustering
  const { clusters: friendClusters } = useFriendClusters({
    locations,
    tempLocations,
    myTempLocations,
    mapZoom,
    mapFilters,
    user,
    listMemberIds,
  });

  // Handle zoom to cluster when clicking on a clustered marker
  const handleZoomToCluster = useCallback((center: [number, number], zoom: number) => {
    if (mapInstance) {
      mapInstance.flyTo(center, zoom, {
        duration: 0.5, // Smooth animation duration in seconds
      });
    }
  }, [mapInstance]);

  // Get language for routing
  const language = localStorage.getItem('language') as 'fr' | 'en' || 'fr';

  // Search places function for RoutePanel
  const searchPlacesForRouting = async (query: string): Promise<any[]> => {
    if (!query.trim() || query.length < 2) return [];
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiBaseUrl}/search/places?q=${encodeURIComponent(query)}&limit=5`);
      if (!response.ok) return [];
      return await response.json();
    } catch {
      return [];
    }
  };

  return (
    <RouteProvider
      userPosition={userPosition}
      userInfo={user ? {
        firstName: user.first_name,
        lastName: user.last_name,
        profilePhoto: user.profile_photo,
        address: user.address ? {
          latitude: user.address.latitude,
          longitude: user.address.longitude,
          street_address: user.address.street_address,
          city: user.address.city,
        } : undefined,
      } : null}
    >
    <div className="map-page">
      {/* Route trigger - bridges Map state to RouteContext */}
      <RouteTrigger
        destination={pendingRouteDestination}
        onTriggered={() => setPendingRouteDestination(null)}
      />

      {/* Extract route state for hiding overlapping markers */}
      <RouteStateExtractor
        onRouteStateChange={setRouteState}
      />

      {/* Route Panel - Desktop sidebar / Mobile bottom sheet */}
      <RoutePanel
        t={t}
        language={language}
        onSearchPlace={searchPlacesForRouting}
        friends={locations.map(loc => ({
          id: loc.id,
          first_name: loc.first_name,
          last_name: loc.last_name,
          latitude: loc.latitude,
          longitude: loc.longitude,
          city: loc.city,
          profile_photo: loc.profile_photo,
        }))}
      />

      <MapSearchBar
        showSearchBar={showSearchBar}
        setShowSearchBar={(show) => {
          setShowSearchBar(show);
          if (!show) {
            setSearchQuery('');
            setFriendResults([]);
            setPlaceResults([]);
          }
        }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearchChange={handleSearchChange}
        isSearching={isSearching}
        friendResults={friendResults}
        placeResults={placeResults}
        selectFriendFromSearch={selectFriendFromSearch}
        selectPlaceFromSearch={selectPlaceFromSearch}
        getPhotoUrl={getPhotoUrl}
        searchHistory={searchHistory}
        onSelectHistoryItem={(query) => {
          setSearchQuery(query);
          handleSearchChange(query);
        }}
        t={t}
        isPanelOpen={selectedFriendForPanel !== null || selectedListForPanel !== null}
        onClosePanel={() => {
          // Close whichever panel is open and clear history
          if (selectedFriendForPanel !== null) {
            handleCloseFriendPanel();
          } else if (selectedListForPanel !== null) {
            handleCloseListPanel();
          }
        }}
        canGoBack={panelHistory.length > 0}
        onBack={handlePanelBack}
        friends={locations}
        onSearchPlace={searchPlacesForRouting}
      />

      {loading && (
        <div className="map-loading">
          <div className="spinner"></div>
          <p>Loading friends locations...</p>
        </div>
      )}

      {error && <div className="map-error">{error}</div>}

      {geoError && (
        <div className="map-geo-warning">
          <button
            className="geo-warning-close"
            onClick={() => setGeoError('')}
            aria-label="Fermer"
          >
            ✕
          </button>
          <span style={{ whiteSpace: 'pre-line' }}>{geoError}</span>
        </div>
      )}

      {!loading && locations.length === 0 && showNoFriendsPanel && (
        <div className="map-empty">
          <button
            className="map-empty-close"
            onClick={() => setShowNoFriendsPanel(false)}
            aria-label="Close"
          >
            ×
          </button>
          <h2>No Friends on the Map Yet</h2>
          <p>
            <span
              onClick={() => navigate('/friends')}
              style={{ color: '#4a90e2', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Add friends
            </span>{' '}
            to see their locations on the map!
          </p>
        </div>
      )}

      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        className={theme === 'dark' ? 'leaflet-dark-mode' : ''}
      >
        <MapInstanceCapture onMapReady={setMapInstance} onZoomChange={setMapZoom} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          className={theme === 'dark' ? 'leaflet-tiles-dark' : ''}
        />

        {/* Route polyline - displays calculated routes on the map */}
        <RoutePolyline language={language} />

        {/* User's GPS location marker (blue/orange dot) - always on top */}
        {/* Hide when GPS is route origin (RoutePolyline renders the route marker) */}
        {userPosition && routeState.originMarkerType !== 'gps' && (
          <Marker
            position={userPosition}
            icon={userIcon}
            zIndexOffset={10000}
            eventHandlers={{
              click: async () => {
                // Perform reverse geocoding to get address via backend proxy
                try {
                  const [lat, lng] = userPosition;
                  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                  const response = await fetch(
                    `${apiBaseUrl}/search/reverse?lat=${lat}&lon=${lng}`
                  );
                  const data = await response.json();

                  // Store the current position place data
                  setCurrentPositionPlace({
                    lat: lat.toString(),
                    lon: lng.toString(),
                    display_name: data.display_name || t('map.gps.myCurrentPosition'),
                    address: data.address,
                  });

                  // Close any selected place panel
                  setSelectedPlace(null);
                  setSelectedMapPlace(null);
                } catch (error) {
                  console.error('Reverse geocoding failed:', error);
                }
              },
            }}
          >
            <Popup>
              <div className="map-popup">
                <h3>{t('map.gps.youAreHere')}</h3>
                <p>{t('map.gps.realTime')}</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* My permanent address marker (only if not in a cluster and no list filter active) */}
        {/* Hide when user address is route origin (RoutePolyline renders the route marker) */}
        {mapFilters.showMyTempLocations &&
         !mapFilters.selectedListId &&
         routeState.originMarkerType !== 'userAddress' &&
         user?.address?.latitude && user?.address?.longitude && (() => {
          // Check if user's address is part of a multi-person cluster
          const isInMultiCluster = friendClusters.some(
            cluster => cluster.friends.length > 1 && cluster.friends.some(f => f.id === user.id)
          );
          // If in a multi-cluster, don't render individually (will be rendered by ClusteredMarker)
          if (isInMultiCluster) return null;

          return (
            <Marker
              position={[user.address.latitude, user.address.longitude]}
              icon={createFriendIcon(
                getPhotoUrl(user.profile_photo),
                user.first_name,
                user.last_name,
                false, // isRoutePoint - never route point here, RoutePolyline handles it
                false, // isRouteDestination
                undefined, // stepNumber
                '#e1723c' // customBorderColor - orange from charte graphique
              )}
            >
              <Popup>
                <div className="map-popup">
                  {user.profile_photo && (
                    <img
                      src={getPhotoUrl(user.profile_photo) || undefined}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="popup-photo"
                    />
                  )}
                  <h3>{t('map.myAddress.permanent')}</h3>
                  <p><strong>{user.first_name} {user.last_name}</strong></p>
                  {user.address.city && <p>{user.address.city}</p>}
                  {user.address.street_address && (
                    <p style={{ fontSize: '12px', color: '#666' }}>{user.address.street_address}</p>
                  )}

                  <div className="popup-buttons">
                    <button
                      className="popup-icon-button contact-icon"
                      onClick={() => setSelectedFriendForPanel(user.id)}
                      title={t('map.popup.viewProfile') || 'Voir ma fiche'}
                    >
                      👤
                    </button>
                    {user.address && user.address.latitude && user.address.longitude && (
                      <button
                        className="popup-icon-button directions-icon"
                        onClick={() => openDirectionsFromPopup({
                          lat: user.address!.latitude!,
                          lng: user.address!.longitude!,
                          name: `${user.first_name} ${user.last_name}`,
                          address: user.address!.city ? `${user.address!.city}, ${user.address!.country || ''}` : undefined,
                        })}
                        title="Itinéraire"
                      >
                        <img
                          src="/itinéraire-icon-blanc.png"
                          alt="Itinéraire"
                          style={{
                            width: '20px',
                            height: '20px'
                          }}
                        />
                      </button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })()}

        {/* All location markers with clustering (friends, their temp locations, and my temp locations) */}
        {friendClusters
          // Exclude single-member clusters that are my own locations (rendered by dedicated markers below)
          .filter(cluster => {
            if (cluster.friends.length === 1) {
              const singleMember = cluster.friends[0];
              // Exclude my temp locations (rendered by MyTempLocationMarker)
              const isMyTempLocation = singleMember.id >= 2000000;
              // Exclude my permanent address (rendered by explicit marker)
              const isMyPermanentAddress = user && singleMember.id === user.id;
              return !isMyTempLocation && !isMyPermanentAddress;
            }
            return true; // Keep all multi-member clusters
          })
          .map((cluster) => (
          <ClusteredMarker
            key={cluster.id}
            cluster={cluster}
            mapZoom={mapZoom}
            onZoomToCluster={handleZoomToCluster}
            getPhotoUrl={getPhotoUrl}
            highlightedFriendId={highlightedFriendId}
            highlightedTempLocationId={highlightedTempLocationId}
            onContactClick={handleContactClick}
            onDirectionsClick={openDirectionsFromPopup}
            loadingFriend={loadingFriend}
            t={t}
            tempLocations={tempLocations}
            onTempLocationClick={handleTempLocationClick}
            hiddenFriendIds={routeState.routeFriendIds}
          />
        ))}


        {/* My temporary location markers (only for isolated/single markers, not in clusters, and no list filter active) */}
        {myTempLocations.map((myTempLoc) => {
          // Filter independently: show all my locations (past, current, future) if enabled
          // Also hide when viewing a specific list (list is about friends, not user)
          if (!mapFilters.showMyTempLocations || mapFilters.selectedListId) return null;

          // Check if this temp location is in a multi-member cluster (2+ markers)
          // If so, it will be rendered by ClusteredMarker, not here
          const myTempLocClusterId = myTempLoc.id + 2000000;
          const isInMultiCluster = friendClusters.some(cluster =>
            cluster.friends.length > 1 &&
            cluster.friends.some(f => f.id === myTempLocClusterId)
          );

          // If in a multi-cluster, don't render individually (ClusteredMarker will render it)
          if (isInMultiCluster) return null;

          const locationType = LOCATION_TYPES.find(lt => lt.value === myTempLoc.type) || LOCATION_TYPES.find(lt => lt.value === 'other')!;

          return (
            <MyTempLocationMarker
              key={`my-temp-${myTempLoc.id}`}
              tempLocation={myTempLoc}
              locationType={locationType}
              locationTypes={LOCATION_TYPES}
              userName={`${user?.first_name || ''} ${user?.last_name || ''}`}
              userPhoto={user?.profile_photo}
              getPhotoUrl={getPhotoUrl}
              shouldOpenPopup={highlightedTempLocationId === myTempLoc.id}
              onEdit={() => {
                // Pre-fill form with temp location data
                setTempLocationData({
                  id: myTempLoc.id,
                  latitude: myTempLoc.latitude,
                  longitude: myTempLoc.longitude,
                  name: myTempLoc.name,
                  description: myTempLoc.description,
                  start_datetime: myTempLoc.start_datetime,
                  end_datetime: myTempLoc.end_datetime,
                  type: myTempLoc.type,
                  visibility_type: myTempLoc.visibility_type,
                  visible_group_ids: myTempLoc.visible_group_ids || [],
                  visible_friend_ids: myTempLoc.visible_friend_ids || [],
                  is_active: myTempLoc.is_active,
                });
                // Pre-fill address if available
                if (myTempLoc.address) {
                  setSkipNextSearch(true);
                  setAddressSearchQuery(myTempLoc.address);
                }
                setShowTempLocationModal(true);
              }}
              onDirections={openDirectionsFromPopup}
              onToggleActive={async (id, currentStatus) => {
                try {
                  await temporaryLocationsAPI.update(id, { ...myTempLoc, is_active: !currentStatus });
                  loadMyTempLocations();
                } catch (error) {
                  console.error('Failed to toggle temp location status:', error);
                }
              }}
            />
          );
        })}

        {/* Location selector for adding temporary locations */}
        <LocationSelector
          isSelecting={isSelectingLocation}
          onLocationSelect={handleMapClick}
          onMapClick={() => {
            // Close search bar on mobile when clicking on the map
            if (window.innerWidth <= 768 && showSearchBar && !searchQuery.trim()) {
              setShowSearchBar(false);
            }
          }}
        />

        {/* Preview marker when selecting location */}
        {previewMarkerPosition && (
          <Marker
            position={previewMarkerPosition}
            icon={previewIcon}
            draggable={true}
            eventHandlers={{
              dragend: async (e) => {
                const marker = e.target;
                const position = marker.getLatLng();
                setPreviewMarkerPosition([position.lat, position.lng]);

                // Update reverse geocoding when marker is dragged via backend proxy
                try {
                  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                  const response = await fetch(
                    `${apiBaseUrl}/search/reverse?lat=${position.lat}&lon=${position.lng}`
                  );
                  const data = await response.json();

                  setSelectedMapPlace({
                    lat: position.lat.toString(),
                    lon: position.lng.toString(),
                    display_name: data.display_name || 'Lieu sélectionné',
                    address: data.address,
                  });
                } catch (error) {
                  console.error('Reverse geocoding failed:', error);
                }
              },
            }}
          >
            <Popup>
              <div className="map-popup">
                <p>{t('map.tempLocation.new')}</p>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {t('map.locationSelect.dragInstruction')}
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Selected place marker */}
        {selectedPlace && (
          <Marker
            position={[parseFloat(selectedPlace.lat), parseFloat(selectedPlace.lon)]}
            icon={selectedPlaceIcon}
          >
            <Popup>
              <div className="map-popup">
                <h3>{selectedPlace.display_name.split(',')[0]}</h3>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Center on user position button */}
      <button
        className="center-position-button"
        onClick={centerOnUserPosition}
        title={t('map.buttons.centerOnMe')}
        style={panelHeightVh > 0 && panelHeightVh < 100 ? { bottom: `calc(${panelHeightVh}vh + 25px)` } : undefined}
      >
        <span className="center-button-text">Centrer sur ma position</span>
        <span className="location-icon">📍</span>
      </button>

      {/* Add temporary location button */}
      <button
        className={`add-temp-location-button ${isSelectingLocation ? 'active' : ''}`}
        onClick={handleOpenTempLocationModal}
        title={t('map.buttons.addTempLocation')}
        style={panelHeightVh > 0 && panelHeightVh < 100 ? { bottom: `calc(${panelHeightVh}vh + 80px)` } : undefined}
      >
        <span className="temp-location-button-text">Ajouter un lieu temporaire</span>
        <span className="location-icon">📌</span>
      </button>

      {/* Map Filters Toggle Button */}
      <button
        className="toggle-filters-button"
        onClick={() => {
          setShowFiltersPanel(!showFiltersPanel);
          if (!showFiltersPanel) {
            setIsFiltersExpanded(true); // Auto-expand filters when opening panel
          }
        }}
        title={t('map.buttons.filters')}
        style={panelHeightVh > 0 && panelHeightVh < 100 ? { bottom: `calc(${panelHeightVh}vh + 135px)` } : undefined}
      >
        <span className="filters-button-text">Filtres de la carte</span>
        <span className="location-icon">🔻</span>
      </button>

      {/* Friend Filter Badge */}
      {mapFilters.selectedFriendId !== null && (() => {
        const selectedFriend = locations.find(loc => loc.id === mapFilters.selectedFriendId);
        if (!selectedFriend) return null;

        return (
          <div className="friend-filter-badge">
            <span className="friend-filter-label">
              📍 {selectedFriend.first_name} {selectedFriend.last_name}
            </span>
            <button
              className="friend-filter-remove"
              onClick={() => {
                setMapFilters({
                  ...mapFilters,
                  selectedFriendId: null,
                });
              }}
              title="Afficher tous les amis"
            >
              ✕
            </button>
          </div>
        );
      })()}

      {/* List Filter Badge */}
      {mapFilters.selectedListId !== null && selectedListForPanel && (
        <div className="friend-filter-badge list-filter-badge">
          <span className="friend-filter-label">
            📋 {t('map.filters.listActive')}
          </span>
          <button
            className="friend-filter-remove"
            onClick={handleCloseListPanel}
            title={t('map.filters.showAll')}
          >
            ✕
          </button>
        </div>
      )}

      {/* Map Filters Panel */}
      <div
        ref={filtersPanelRef}
        className={`map-filters-panel ${showFiltersPanel ? 'open' : ''}`}
        style={panelHeightVh > 0 && panelHeightVh < 100 ? { bottom: `calc(${panelHeightVh}vh + 20px)` } : undefined}
      >
        <button
          className="filters-toggle-header"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
        >
          <span className={`filters-triangle ${isFiltersExpanded ? 'expanded' : ''}`}>🔻</span>
          <span>{t('map.filters.title')}</span>
        </button>

        {isFiltersExpanded && (
          <div className="filters-content">
            {/* Friends filters section */}
            <div className="filter-section">
              <label className="filter-checkbox-item filter-section-label-with-checkbox">
                <input
                  type="checkbox"
                  checked={
                    mapFilters.showPermanentAddresses &&
                    mapFilters.showCurrentTempLocations &&
                    mapFilters.showUpcomingTempLocations
                  }
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setMapFilters({
                      ...mapFilters,
                      showPermanentAddresses: newValue,
                      showCurrentTempLocations: newValue,
                      showUpcomingTempLocations: newValue,
                    });
                  }}
                />
                <span className="filter-section-label">{t('map.filters.myNetwork')}</span>
              </label>
              <div className="filter-options">
                <label className="filter-checkbox-item filter-sub-item">
                  <input
                    type="checkbox"
                    checked={mapFilters.showPermanentAddresses}
                    onChange={(e) => setMapFilters({ ...mapFilters, showPermanentAddresses: e.target.checked })}
                  />
                  <span className="filter-label">📍 {t('map.filters.permanentAddresses')}</span>
                </label>
                <label className="filter-checkbox-item filter-sub-item">
                  <input
                    type="checkbox"
                    checked={mapFilters.showCurrentTempLocations}
                    onChange={(e) => setMapFilters({ ...mapFilters, showCurrentTempLocations: e.target.checked })}
                  />
                  <span className="filter-label">⏰ {t('map.filters.currentTempLocations')}</span>
                </label>
                <label className="filter-checkbox-item filter-sub-item">
                  <input
                    type="checkbox"
                    checked={mapFilters.showUpcomingTempLocations}
                    onChange={(e) => setMapFilters({ ...mapFilters, showUpcomingTempLocations: e.target.checked })}
                  />
                  <span className="filter-label">📅 {t('map.filters.upcomingTempLocations')}</span>
                </label>
              </div>
            </div>

            {/* My addresses filter */}
            <div className="filter-options" style={{ marginTop: '15px' }}>
              <label className="filter-checkbox-item">
                <input
                  type="checkbox"
                  checked={mapFilters.showMyTempLocations}
                  onChange={(e) => setMapFilters({ ...mapFilters, showMyTempLocations: e.target.checked })}
                />
                <span className="filter-label">🏠 {t('map.filters.myAddresses')}</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Selection mode overlay */}
      {isSelectingLocation && (
        <div className="selection-mode-overlay">
          <div className="selection-instructions">
            <h3>{t('map.locationSelect.title')}</h3>
            {!previewMarkerPosition ? (
              <p>{t('map.locationSelect.clickInstruction')}</p>
            ) : (
              <p>{t('map.locationSelect.dragInstruction')}</p>
            )}
            {previewMarkerPosition && (
              <div className="selection-actions">
                <button className="btn-confirm-location" onClick={handleConfirmLocation}>
                  ✓ {t('map.form.confirm')}
                </button>
                <button className="btn-cancel-location" onClick={handleCancelSelection}>
                  ✕ {t('map.form.cancel')}
                </button>
              </div>
            )}
            {!previewMarkerPosition && (
              <button className="btn-cancel-location" onClick={handleCancelSelection}>
                ✕ {t('map.form.cancel')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add Friend to Group Modal */}
      <AddToGroupModal
        isOpen={showAddFriendToGroupModal}
        onClose={() => setShowAddFriendToGroupModal(false)}
        groups={groups}
        selectedGroupsForFriend={selectedGroupsForFriend}
        onToggleGroupSelection={toggleGroupSelection}
        onAddFriendToGroups={handleAddFriendToGroups}
        loading={loading}
        t={t}
      />

      {/* Temporary Location Form */}
      <TempLocationFormManager
        t={t}
        locations={locations}
        friendGroups={friendGroups}
        groups={groups}
        showTempLocationModal={showTempLocationModal}
        setShowTempLocationModal={setShowTempLocationModal}
        tempLocationData={tempLocationData}
        setTempLocationData={setTempLocationData}
        addressSearchQuery={addressSearchQuery}
        setAddressSearchQuery={setAddressSearchQuery}
        isSearchingAddress={isSearchingAddress}
        addressSuggestions={addressSuggestions}
        selectAddress={selectAddress}
        onRefreshTempLocations={loadTempLocations}
        onRefreshMyTempLocations={loadMyTempLocations}
        onCreateGroup={handleCreateGroupFromTempModal}
        onCancel={() => {
          // Restore place panel if modal was closed without saving
          if (pendingPlacePanel) {
            setSelectedPlace(pendingPlacePanel);
            setPendingPlacePanel(null);
          }
        }}
        onSaveSuccess={() => {
          // Clear pending place panel when successfully saved
          setPendingPlacePanel(null);
        }}
      />

      {/* Selected Place Side Panel */}
      {selectedPlace && (
        <div className="place-info-panel">
          <button
            className="panel-close"
            onClick={() => setSelectedPlace(null)}
            title={t('common.close')}
          >
            ✕
          </button>
          <div className="place-info-header">
            <h3>📍 {selectedPlace.name || selectedPlace.display_name.split(',')[0]}</h3>
          </div>
          <div className="place-info-content">
            {/* Structured address if available */}
            {selectedPlace.address && (
              <div className="place-info-section" style={{ position: 'relative', paddingRight: '40px' }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Adresse :</strong>
                </p>
                {selectedPlace.address.housenumber && selectedPlace.address.street && (
                  <p style={{ fontSize: '14px', marginBottom: '5px' }}>
                    {selectedPlace.address.housenumber} {selectedPlace.address.street}
                  </p>
                )}
                {!selectedPlace.address.housenumber && selectedPlace.address.street && (
                  <p style={{ fontSize: '14px', marginBottom: '5px' }}>
                    {selectedPlace.address.street}
                  </p>
                )}
                {selectedPlace.address.postcode && selectedPlace.address.city && (
                  <p style={{ fontSize: '14px', marginBottom: '5px' }}>
                    {selectedPlace.address.postcode} {selectedPlace.address.city}
                  </p>
                )}
                {!selectedPlace.address.postcode && selectedPlace.address.city && (
                  <p style={{ fontSize: '14px', marginBottom: '5px' }}>
                    {selectedPlace.address.city}
                  </p>
                )}
                {selectedPlace.address.country && (
                  <p style={{ fontSize: '13px', color: '#999' }}>
                    {selectedPlace.address.country}
                  </p>
                )}
                <button
                  onClick={() => {
                    const addressText = selectedPlace.display_name;
                    navigator.clipboard.writeText(addressText);
                    // Visual feedback
                    const btn = document.activeElement as HTMLElement;
                    const originalHTML = btn.innerHTML;
                    btn.innerHTML = '<span style="color: #4CAF50; font-size: 16px;">✓</span>';
                    setTimeout(() => {
                      btn.innerHTML = originalHTML;
                    }, 1000);
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '0',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#999',
                    padding: '6px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#333' : '#f0f0f0';
                    e.currentTarget.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#999';
                  }}
                  title="Copier l'adresse complète"
                  aria-label="Copier l'adresse complète"
                >
                  <img
                    src={theme === 'dark' ? '/copier-icon-blanc.png' : '/copier-icon-noir.png'}
                    alt="Copier"
                    style={{
                      width: '16px',
                      height: '16px',
                      opacity: 0.6
                    }}
                  />
                </button>
              </div>
            )}

            {/* Fallback to display_name if no structured address */}
            {!selectedPlace.address && (
              <div className="place-info-section">
                <p>
                  <strong>Adresse :</strong>
                </p>
                <p style={{ fontSize: '13px', color: '#666' }}>
                  {selectedPlace.display_name}
                </p>
              </div>
            )}

            {/* Show score and confidence if available (for debugging) */}
            {selectedPlace.score && (
              <div className="place-info-section" style={{ marginTop: '10px', fontSize: '12px', color: '#999' }}>
                Score: {selectedPlace.score} | Confiance: {selectedPlace.confidence}
              </div>
            )}

            <div className="place-coordinates">
              <p>
                <strong>Coordonnées :</strong>
              </p>
              <p className="coords-text">
                {parseFloat(selectedPlace.lat).toFixed(6)}, {parseFloat(selectedPlace.lon).toFixed(6)}
              </p>
            </div>
          </div>
          <div className="place-info-actions">
            <button
              className="btn-add-as-temp-location"
              onClick={handleAddPlaceAsTempLocation}
            >
              <span className="button-icon">📌</span>
              {t('map.form.addAsTempLocation')}
            </button>
          </div>
        </div>
      )}

      {/* Current Position Side Panel */}
      {currentPositionPlace && (
        <div className="place-info-panel">
          <button
            className="panel-close"
            onClick={() => setCurrentPositionPlace(null)}
            title={t('common.close')}
          >
            ✕
          </button>
          <div className="place-info-header">
            <h3>{t('map.gps.currentPositionTitle')}</h3>
          </div>
          <div className="place-info-content">
            <div className="place-coordinates">
              <p>
                <strong>Coordonnées :</strong>
              </p>
              <p className="coords-text">
                {parseFloat(currentPositionPlace.lat).toFixed(6)}, {parseFloat(currentPositionPlace.lon).toFixed(6)}
              </p>
            </div>
          </div>
          <div className="place-info-actions">
            <button
              className="btn-add-as-temp-location"
              onClick={handleAddCurrentPositionAsTempLocation}
            >
              <span className="button-icon">📌</span>
              {t('map.form.addAsTempLocation')}
            </button>
          </div>
        </div>
      )}

      {/* Selected Map Place Side Panel */}
      {selectedMapPlace && (
        <div className="place-info-panel">
          <button
            className="panel-close"
            onClick={() => {
              console.log('✕ Closing selectedMapPlace panel');
              setSelectedMapPlace(null);
              setIsSelectingLocation(false);
              setPreviewMarkerPosition(null);
            }}
            title={t('common.close')}
          >
            ✕
          </button>
          <div className="place-info-header">
            <h3>📍 Lieu sélectionné</h3>
          </div>
          <div className="place-info-content">
            <div className="place-info-section">
              <p>
                <strong>Adresse :</strong>
              </p>
              <p style={{ fontSize: '13px', color: '#666' }}>
                {selectedMapPlace.display_name}
              </p>
            </div>
            <div className="place-coordinates">
              <p>
                <strong>Coordonnées :</strong>
              </p>
              <p className="coords-text">
                {parseFloat(selectedMapPlace.lat).toFixed(6)}, {parseFloat(selectedMapPlace.lon).toFixed(6)}
              </p>
            </div>
            <p className="hint-text" style={{ fontSize: '13px', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
              {t('map.gps.dragMarkerHint')}
            </p>
          </div>
          <div className="place-info-actions">
            <button
              className="btn-add-as-temp-location"
              onClick={handleAddSelectedMapPlaceAsTempLocation}
            >
              <span className="button-icon">📌</span>
              {t('map.form.createButton')}
            </button>
          </div>
        </div>
      )}

      {/* Friend Profile Panel - New Bottom Sheet */}
      {selectedFriendForPanel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          pointerEvents: 'none'
        }}>
          <div style={{ pointerEvents: 'auto' }}>
            <FriendProfilePanel
              friendId={selectedFriendForPanel}
              onClose={handleCloseFriendPanel}
              onAddressClick={handlePanelAddressClick}
              onHeightChange={setPanelHeightVh}
              onListClick={handleListClick}
              onBack={panelHistory.length > 0 ? handlePanelBack : undefined}
              canGoBack={panelHistory.length > 0}
            />
          </div>
        </div>
      )}

      {/* List Panel - Shows list details with members */}
      {selectedListForPanel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          pointerEvents: 'none'
        }}>
          <div style={{ pointerEvents: 'auto' }}>
            <ListPanel
              listId={selectedListForPanel}
              onClose={handleCloseListPanel}
              onMemberClick={handleListMemberClick}
              onAddMember={handleOpenAddMembersModal}
              onHeightChange={setPanelHeightVh}
              onBack={panelHistory.length > 0 ? handlePanelBack : undefined}
              canGoBack={panelHistory.length > 0}
              refreshKey={listPanelRefreshKey}
              newMemberIds={newlyAddedMemberIds}
              onNewMembersAnimated={() => setNewlyAddedMemberIds([])}
            />
          </div>
        </div>
      )}

      {/* Add Members to List Modal */}
      <AddMembersModal
        isOpen={showAddMembersToListModal}
        onClose={() => setShowAddMembersToListModal(false)}
        friends={getAvailableFriendsForList()}
        selectedIds={selectedFriendIdsForList}
        onToggleSelection={toggleFriendSelectionForList}
        onConfirm={handleAddMembersToList}
        loading={loading}
        getPhotoUrl={getPhotoUrl}
      />

      {/* Welcome Modal - shown after registration */}
      {showWelcomeModal && (
        <div className="welcome-modal-overlay" onClick={() => setShowWelcomeModal(false)}>
          <div className="welcome-modal" onClick={(e) => e.stopPropagation()}>
            <div className="welcome-modal-icon">🎉</div>
            <h2>Bienvenue sur Bob Map !</h2>
            <p>Ton compte est prêt. Commence par ajouter des amis pour voir où ils habitent sur la carte.</p>
            <div className="welcome-modal-actions">
              <button
                className="btn-welcome-primary"
                onClick={() => {
                  setShowWelcomeModal(false);
                  navigate('/friends?tab=search');
                }}
              >
                Trouver des amis
              </button>
              <button
                className="btn-welcome-secondary"
                onClick={() => setShowWelcomeModal(false)}
              >
                Explorer la carte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </RouteProvider>
  );
};

export default Map;
